<template>
  <div
    class="roomie-phone-input"
    :class="`roomie-phone-input--${currentVariant}`">
    <label
      class="roomie-phone-input__label right"
      :for="id">
      {{ numberLabel }}
    </label>

    <label class="roomie-phone-input__label">
      {{ countryLabel }}
    </label>

    <div class="roomie-phone-input__input-container">
      <input
        :id="id"
        ref="phoneInput"
        type="tel"
        class="w-full roomie-phone-input__input"
        :name="name"
        :required="isRequired"
        :disabled="isDisabled"
        :autofocus="autofocus"
        @input="onInputChange($event)"
        @change="onInputChange()"
        @focus="$emit('focus')"
        @blur="$emit('blur')">

      <span class="roomie-phone-input__icon roomie-icon">
        <SuccessIcon v-if="isValid" />
        <ErrorIcon v-if="!isValid" />
      </span>
    </div>

    <p class="roomie-phone-input__helper-text">
      {{ externalError || error || successMessage }}
    </p>
  </div>
</template>

<script>
import SuccessIcon from '../../assets/icons/v2/verified.svg'
import ErrorIcon from '../../assets/icons/v2/error.svg'
import intlTelInput from 'intl-tel-input'
import { SPANISH_LOCALIZED_COUNTRIES } from './utils'

export { VARIANT } from './utils'

export default {
  name: 'RoomiePhoneInput',
  components: {
    SuccessIcon,
    ErrorIcon
  },
  props: {
    id: {
      type: String,
      default: 'roomie-phone-input',
      required: true
    },
    name: {
      type: String,
      default: 'roomie-phone-input',
      required: true
    },
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    isRequired: {
      type: Boolean,
      default: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    initialCountry: {
      type: String,
      required: true,
      validator: value => ['co', 'us', 'mx'].includes(value)
    },
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'success', 'error', 'warning'].includes(value)
    },
    numberLabel : {
      type: String,
      required: true
    },
    countryLabel: {
      type: String,
      required: true
    },
    successLabel: {
      type: String,
      default: 'Número válido',
      required: false
    },
    errorLabel: {
      type: String,
      default: 'Revisa el país o el número ingresado',
      required: false
    },
    autofocus: {
      type: Boolean,
      required: false,
      default: false
    },
    preferredCountries: {
      type: Array,
      default: () => ['co', 'us', 'mx']
    },
    externalError: {
      type: String,
      required: false,
      default: ''
    },
  },
  emits: ['input', 'focus', 'blur', 'full', 'is-valid', 'error'],
  data: () => ({
    intlTelInput: null,
    isValid: false,
    error: null,
    successMessage: null,
    currentVariant: ''
  }),
  watch: {
    value (newValue) {
      this.intlTelInput.setNumber(newValue || '')
    },
    externalError () {
      this.validate()
    }
  },
  async mounted () {
    this.intlTelInput = intlTelInput(this.$refs.phoneInput, {
      initialCountry: this.initialCountry,
      autoPlaceholder: 'aggressive',
      separateDialCode: true,
      preferredCountries: this.preferredCountries,
      utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/18.1.1/js/utils.js',
      formatOnDisplay: true,
      localizedCountries: SPANISH_LOCALIZED_COUNTRIES
    })

    await this.intlTelInput.promise

    if (this.value) {
      this.intlTelInput.setNumber(this.value)
      this.validate()
      this.$emit('full', this.getPhoneData())
    }

    this.$refs.phoneInput.addEventListener('countrychange', this.onInputChange)
    this.$refs.phoneInput.addEventListener('change', this.onInputChange)
    this.$refs.phoneInput.addEventListener('keyup', this.onInputChange)
  },
  beforeDestroy () {
    this.$refs.phoneInput.removeEventListener('countrychange', this.onInputChange)
    this.$refs.phoneInput.removeEventListener('change', this.onInputChange)
    this.$refs.phoneInput.removeEventListener('keyup', this.onInputChange)
    this.intlTelInput.destroy()
  },
  methods: {
    onInputChange ($event) {
      let value = this.getPhoneData()

      if ($event) {
        value.numberWithoutCode = $event.target.value
        $event.target.value = this.sanitizePhone($event.target.value)
      }

      this.$emit('input', value.numberWithCode)
      this.$emit('full', value)

      this.validate()
    },
    sanitizePhone(phoneNumber) {
      return phoneNumber.split('').map((character) => /\d|-/g.test(character) ? character : '').join('')
    },
    getPhoneData () {
      const value = {
        numberWithCode: this.intlTelInput.getNumber() || '',
        code: this.intlTelInput ? `+${this.intlTelInput.getSelectedCountryData().dialCode}` : ''
      }

      value.phoneNumber = value.numberWithCode.replace(value.code, '')
      return value
    },
    validate () {
      this.isValid = this.intlTelInput.isValidNumber()

      if (this.externalError) this.isValid = false

      this.error = this.isValid ? null : this.errorLabel
      this.successMessage = this.isValid ? this.successLabel : null
      this.currentVariant = this.isValid ? 'success' : 'error'
      this.$emit('is-valid', this.isValid)
      this.$emit('error', this.error)
    }
  }
}
</script>

<style src='./_index.scss' lang='scss'/>
